import { useId } from 'react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

type SectionProps = {
  /** Small section heading. Omit for an untitled group of rows. */
  title?: ReactNode
  description?: ReactNode
  /** Right-aligned slot on the heading line (a hint, a "Saved" tick…). */
  aside?: ReactNode
  className?: string
  children: ReactNode
}

/** A titled group on a settings page. Usually wraps one `SettingsCard`. */
export function SettingsSection({
  title,
  description,
  aside,
  className,
  children,
}: SectionProps) {
  const headingId = useId()
  return (
    <section
      aria-labelledby={title ? headingId : undefined}
      className={cn('flex flex-col gap-2', className)}
    >
      {(title || aside) && (
        <div className="flex min-h-5 items-end justify-between gap-3 px-0.5">
          <div className="min-w-0">
            {title && (
              <h2 id={headingId} className="text-[13px] font-semibold text-foreground">
                {title}
              </h2>
            )}
            {description && (
              <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
            )}
          </div>
          {aside && (
            <div className="shrink-0 text-xs text-muted-foreground">{aside}</div>
          )}
        </div>
      )}
      {children}
    </section>
  )
}

type CardProps = {
  className?: string
  children: ReactNode
}

/** White rounded surface; rows inside are separated by hairlines. */
export function SettingsCard({ className, children }: CardProps) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-[14px] border bg-card text-card-foreground divide-y divide-border',
        className,
      )}
    >
      {children}
    </div>
  )
}
